/** Jackpot meter panel showing 4 tiers (9 down to 6) with badge icons and dollar values */

import { Container, Graphics, Text, TextStyle, Sprite, Assets } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('JackpotPanel');

interface TierConfig {
  tier: number;
  value: number;
  badge: string;
  scale: number;
  rowW: number;
  rowH: number;
}

export class JackpotPanel extends Container {
  private _rows: Container[] = [];
  private _valueTexts: Text[] = [];

  constructor() {
    super();

    // Progressive sizing: top row biggest, bottom smallest — large pill rows
    const tiers: TierConfig[] = [
      { tier: 9, value: 2000, badge: 'assets/sprites/jp_9.png', scale: 1.0, rowW: 380, rowH: 60 },
      { tier: 8, value: 200, badge: 'assets/sprites/jp_8.png', scale: 0.85, rowW: 320, rowH: 52 },
      { tier: 7, value: 50, badge: 'assets/sprites/jp_7.png', scale: 0.75, rowW: 280, rowH: 46 },
      { tier: 6, value: 20, badge: 'assets/sprites/jp_6.png', scale: 0.65, rowW: 250, rowH: 40 },
    ];

    const gap = 7;
    const maxRowW = tiers[0].rowW; // Right-align to the widest row
    let yOffset = 0;

    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      const row = new Container();

      // Right-align: offset x so right edges align
      row.x = maxRowW - t.rowW;
      row.y = yOffset;
      yOffset += t.rowH + gap;

      const borderRadius = t.rowH / 2;

      // Dark blue rounded-rect pill background
      const bg = new Graphics();
      bg.roundRect(0, 0, t.rowW, t.rowH, borderRadius);
      bg.fill({ color: 0x080f32, alpha: 0.88 });
      bg.roundRect(0, 0, t.rowW, t.rowH, borderRadius);
      bg.stroke({ color: 0x3c64b4, width: 1.5, alpha: 0.5 });
      row.addChild(bg);

      // Badge image (load async)
      this._loadBadge(row, t.badge, t.rowH);

      // Currency value text — white bold, right-aligned (larger fonts)
      const fontSize = Math.round(22 * t.scale + 6);
      const valueText = new Text({
        text: `$${t.value.toFixed(2)}`,
        style: new TextStyle({
          fontFamily: 'Arial',
          fontSize,
          fill: 0xffffff,
          fontWeight: 'bold',
        }),
      });
      valueText.anchor.set(1, 0.5);
      valueText.x = t.rowW - 14;
      valueText.y = t.rowH / 2;
      row.addChild(valueText);
      this._valueTexts.push(valueText);

      this.addChild(row);
      this._rows.push(row);
    }

    logger.info('JackpotPanel created with 4 progressive tiers');
  }

  private async _loadBadge(row: Container, path: string, rowH: number): Promise<void> {
    try {
      const tex = await Assets.load(path);
      if (tex) {
        const badge = new Sprite(tex);
        badge.anchor.set(0, 0.5);
        badge.x = 6;
        badge.y = rowH / 2;
        const scale = (rowH - 2) / tex.height;
        badge.scale.set(scale);
        row.addChildAt(badge, 1); // above bg, below text
        logger.debug(`Badge loaded: ${path}`);
      }
    } catch {
      logger.warn(`Failed to load badge: ${path}`);
    }
  }

  updateValues(totalBet: number): void {
    const multipliers = [2000, 200, 50, 20];
    // Values are the tier multipliers (fixed jackpot amounts)
    for (let i = 0; i < this._valueTexts.length; i++) {
      this._valueTexts[i].text = `$${multipliers[i].toFixed(2)}`;
    }
  }
}
